import React from "react";
import { rtlLanguages, resources } from "translation/i18n";
import logo from "assets/logo.svg";
import landing from "assets/landing.jpg";
import { API_BASE_URL, CABLE_BASE_URL } from "utils/constants";
import { groups, userDefinedEntities } from "./entities";
import { authConfig, authUserDefinedEntities } from "authEntities";
import { customTheme } from "components/Theme";
import { store } from "./index";
import rootReducer from "./reducers";
import { history } from "configureStore";
import firebaseConfig from "utils/firebaseConfig";
import ConversationHeader from "./components/ConversationHeader";
import ConversationExtras from "./components/ConversationExtras";
import ProgramEndedTag from "./components/ProgramEndedTag";
import UserInfo from "./components/UserInfo";
import InstallAppCard from "./components/InstallAppCard";
import { refreshChatUnreadCount } from "@thewebops/admin-panel";

const storeDispatcher = (type) => {
  store.dispatch({ type });
};

const replaceReducer = (adminPanelReducers) => {
  const nextReducer = rootReducer(history, adminPanelReducers);
  store.replaceReducer(nextReducer);
};

const chatCustomConfigs = {
  enabled: true,
  // A dot on the Chat menu item while the coach has unread messages (#74, api #159).
  unreadBadge: { enabled: true },
  chatOpsWeb: {
    conversationName: (conversation) => (
      <>
        {conversation?.target_participation?.participant?.name}
        <ProgramEndedTag participant={conversation?.target_participation?.participant} />
      </>
    ),
    conversationNameInChat: (conversation) => <ConversationHeader conversation={conversation} />,
    UserInfoModalContent: UserInfo,
    getIdFromConversation: (conversation) => conversation?.target_participation?.participant?.id,
    // Keeps the package's ConversationChannel, and reports the open conversation for pushes (#41).
    conversationExtraComponent: ConversationExtras,
    // The chat shows this between the search box and the conversations, its only slot above the list. It's meant
    // for chatConfigs.filters, which the coaches' app doesn't use: the install / notifications card instead.
    dataFilters: <InstallAppCard />,
    isBroadcastEnabled: false,
    broadcastUserList: [{ label: { en: "Users", ar: "المستخدمين" }, value: "users" }],
    theme: {
      primaryColor: "rgb(0, 46, 58)",
      background: "https://res.cloudinary.com/thewebops/image/upload/v1600629686/background_jvg2z3.jpg",
      smallEmoji: true,
      // Roboto has no Arabic letters, so English stays in Roboto and Arabic falls through to Tajawal.
      fontFamily: "'Roboto', 'Tajawal', sans-serif",
    },
  },
  chatopsActionCable: {
    // The chat sends its requests with its own axios, so the admin panel's "log out on 401" rule doesn't see them (#69).
    onUnauthorized: () => storeDispatcher("logout"),
    apiEndpoints: {
      // Include members whose program ended but who can still chat (api #100).
      fetchConversations: {
        endpoint: () => "/conversations?program_status=all",
        method: "get",
      },
      sendMessage: {
        endpoint: (id) => `/conversations/${id}`,
        method: "PUT",
      },
      fetchMessages: {
        endpoint: (id) => `/conversations/${id}`,
        method: "GET",
      },
      sendTyping: {
        endpoint: (id) => `chat_engine/conversations/${id}/typing`,
        method: "post",
      },
      updateLastAppearance: {
        endpoint: (id) => `chat_engine/conversations/${id}/last_appearance`,
        method: "put",
      },
      fetchContacts: {
        endpoint: () => "users",
        method: "get",
      },
      fetchContact: {
        endpoint: (id) => `users/${id}/info`,
        method: "get",
      },
      broadcast: {
        endpoint: () => "chat_engine/broadcasts",
        method: "post",
      },
    },
    conversationSearchParamName: "username",
  },
  senderType: "Coach",
  nameSpace: "coaches",
  cableURL: CABLE_BASE_URL,
  // Signs in with a single-use ticket (POST /coaches/chat_ticket), not the token and email in the
  // address, where a "+" in the email turned into a space and the chat could not connect.
  cableTicket: true,
};
const notificationConfigs = {
  enabled: true,
  baseURL: `${API_BASE_URL}/coaches`,
  authHeaders: "authHeaders",
  defaultImageUrl: "https://res.cloudinary.com/webopsv2/image/upload/v1636280162/images_raevqs.png",
  // The alert sound plays for each push through the service worker (utils/playNotificationSound). A chat push may
  // mean a new unread message, so the Chat menu dot checks again (#74; reached once #80 is fixed).
  onReceiveNotification: () => refreshChatUnreadCount(),
  displayAsLink: () => "/app/chat",
  // The Nutivate-owned Firebase project, per environment (src/firebaseConfigs.json). Without it, no push.
  firebaseConfig,
  theme: {
    notificationLayout: {
      dropDownAnimation: "opacity",
      displayImages: false,
      iconSize: 1.8,
      itemTheme: "hamza",
      headerHeight: 62,
      top: 140,
      alignment: -57,
      showEmptyIcon: true,
      dropDownHeight: 64.4,
      showArrow: true,
    },
    notificationColors: {
      date: "#505050",
      icon: "#fff",
      iconHover: "#808080",
      scrollBarThumb: "#505050",
      scrollBarTrack: "#f5f5f5",
      unreadNotification: "#40a9ff",
      background: "#fff",
      markAsRead: "#000",
      markAsReadHover: "#000",
    },
  },
};

export const adminConfigs = {
  languageResources: resources,
  disableLocalization: true,
  rtlLanguages,
  logo,
  landingImage: landing,
  fullLogo: logo,
  url: API_BASE_URL,
  groups,
  userDefinedEntities,
  authUserDefinedEntities,
  authConfig,
  theme: customTheme,
  privateRouteURL: "/app",
  storeDispatcher,
  replaceReducer,
  chatConfigs: chatCustomConfigs,
  notificationConfigs,
  //customRoutes,
};
